"use client";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { dataSecurityList, effectivenessList } from "../../../utils/types";
import { z } from "zod";
import { formatOptions } from "../../../utils/helpers";
import Select from "react-select";
import axios from "axios";
import toast from "react-hot-toast";
import { useRequestStore } from "../../../data/stores/loggerStore";

const formSchema = z.object({
  effectivenessPurposeLevel: z.string().min(1, "Required"),
  dataSecurity: z.string().min(1, "Required"),
  supportTainingNeeds: z.string().optional(),
  improvementRequired: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const FitForPurposeEdit = ({ data, setIsEditing }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      effectivenessPurposeLevel: data?.effectivenessPurposeLevel || undefined,
      dataSecurity: data?.dataSecurity || undefined,
      supportTainingNeeds: data?.supportTainingNeeds || undefined,
      improvementRequired: data?.improvementRequired || undefined,
    },
  });
  const dataSecurityOptions = formatOptions(dataSecurityList);
  const effectivenessOptions = formatOptions(effectivenessList);
  const { dataSystemAll, getDynamicLogger }: any = useRequestStore();

  //   console.log(getValues, "value");
  const onSubmit = async (itemForm: any) => {
    try {
      const payload = {
        datasystems: [
          {
            effectivenessPurposeLevel: itemForm.effectivenessPurposeLevel,
            improvementRequired: itemForm.improvementRequired,
            dataSecurity: itemForm.dataSecurity,
            supportTainingNeeds: itemForm.supportTainingNeeds,
            erpModule: data.erpModule,
            _id: data?._id,
          },
        ],
        email: data?.profile?.user?.email,
        user: data?.profile?.user?._id,
      };

      const res = await axios.put(`/api/v1/data-system`, payload);
      const updatedArray = res?.data?.data;

      const newDocs = dataSystemAll.docs.map((d: any) => {
        const updated = updatedArray.find((u: any) => u._id === d._id);
        if (!updated) return d;
        return {
          ...d,
          ...updated,
          profile:
            typeof updated.profile === "string" ? d.profile : updated.profile,
        };
      });
      const updatedDataSystemAll = { ...dataSystemAll, docs: newDocs };
      getDynamicLogger(updatedDataSystemAll, "dataSystemAll");
      toast.success(
        res?.data?.message ||
          "Data system updated, Changes would appear shortly."
      );
      // toast.success(res?.data?.message || "Submitted successfully!");
      // toast.success("Data system updated, Changes would appear shortly.");
      setIsEditing(false);
    } catch (error: any) {
      if (error?.response?.data?.error) {
        error.response.data.error.forEach((err: any) =>
          toast.error(err.message || err.msg)
        );
      } else {
        toast.error(error?.response?.data?.message || "Something went wrong");
      }
    }
  };
  const handleCancel = () => {
    reset({
      effectivenessPurposeLevel: data?.effectivenessPurposeLevel || undefined,
      dataSecurity: data?.dataSecurity || undefined,
      supportTainingNeeds: data?.supportTainingNeeds || undefined,
      improvementRequired: data?.dataCollectionToolPurposeServe || undefined,
    });
    setIsEditing(false);
  };

  return (
    <div className="font-Inter max-w-2xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Level of effectiveness for purpose
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="effectivenessPurposeLevel"
            control={control}
            rules={{ required: "Please select a technology" }}
            render={({ field }) => (
              <Select
                options={effectivenessOptions} // already in { value, label } format
                placeholder="Select Technology..."
                onChange={(selected) => field.onChange(selected?.value)}
                value={
                  effectivenessOptions.find(
                    (opt) => opt.value === field.value
                  ) || null
                }
              />
            )}
          />
          {errors.effectivenessPurposeLevel && (
            <p className="text-red-500 text-xs mt-1">
              {errors.effectivenessPurposeLevel.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Data Security
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="dataSecurity"
            control={control}
            rules={{ required: "Please select a technology" }}
            render={({ field }) => (
              <Select
                options={dataSecurityOptions} // already in { value, label } format
                placeholder="Select Technology..."
                onChange={(selected) => field.onChange(selected?.value)}
                value={
                  dataSecurityOptions.find(
                    (opt) => opt.value === field.value
                  ) || null
                }
              />
            )}
          />
          {errors.dataSecurity && (
            <p className="text-red-500 text-xs mt-1">
              {errors.dataSecurity.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Support and Training Needs
          </label>
          <input
            type="text"
            {...register("supportTainingNeeds")}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.supportTainingNeeds ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Support and Training Needs"
          />
          {errors.supportTainingNeeds && (
            <p className="text-red-500 text-xs mt-1">
              {errors.supportTainingNeeds.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Improvements required
          </label>
          <input
            type="text"
            {...register("improvementRequired")}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.supportTainingNeeds ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Improvements required"
          />
          {errors.improvementRequired && (
            <p className="text-red-500 text-xs mt-1">
              {errors.improvementRequired.message}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6">
          <button
            type="button"
            onClick={handleCancel}
            className="px-12 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Go Back
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-12 py-2.5 bg-[#2070B8] font-medium text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FitForPurposeEdit;
